/*
 * NFT Landing Page - Sailing Ships Collection
 * Copyright (C) 2025 jsaif
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 *
 * Original project: https://github.com/s-shemmee/NFT-Portfolio-Landing-Page
 * Original creator: s-shemmee
 *
 * Hyperspace Tunnel Effect:
 * Based on CodePen by Noah Blon
 * Source: https://codepen.io/noahblon/pen/DpNRyR
 * License: MIT - Used with attribution
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import "./Hyperspace.css";
// Import the original ship images
import Ship from "../images/Zoom.png";
import Ship1 from "../images/Zoom1.png";
import Ship2 from "../images/Zoom2.png";

// WebGL Renderer for Lightspeed Effect
class Renderer {
  #vertexSrc = `attribute vec2 position;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}`;
  #fragmtSrc = `precision highp float;
uniform float time;
uniform vec2 resolution;

#define FC gl_FragCoord.xy
#define R resolution
#define T time
#define hue(a) (0.6+0.6*cos(6.3*(a)+vec3(0.0,83.0,21.0)))

float rnd(float a) {
  vec2 p = fract(a * vec2(12.9898, 78.233)); 
  p += dot(p, p + 345.0);
  return fract(p.x * p.y);
}

vec3 pattern(vec2 uv) {
  vec3 col = vec3(0.0);
  for (float i = 0.0; i < 20.0; i++) {
    float a = rnd(i);
    vec2 n = vec2(a, fract(a * 34.56));
    vec2 p = sin(n * (T + 7.0) + T * 0.5);
    float d = dot(uv - p, uv - p);
    col += 0.00125 / d * hue(dot(uv, uv) + i * 0.125 + T);
  }
  return col;
}

void main() {
  vec2 uv = (FC - 0.5 * R) / min(R.x, R.y);
  vec3 col = vec3(0.0);
  float s = 2.4;
  float a = atan(uv.y, uv.x);
  float b = length(uv);
  
  // Create the tunnel effect - this is the key part
  uv = vec2(a * 5.0 / 6.28318, 0.05 / tan(b) + T);
  uv = fract(uv) - 0.5;
  col += pattern(uv * s);
  
  gl_FragColor = vec4(col, 1.0);
}`;
  #vertices = [-1, 1, -1, -1, 1, 1, 1, -1];

  constructor(canvas, scale) {
    this.canvas = canvas;
    this.scale = scale;

    // Try WebGL first, then WebGL2 as fallback
    this.gl =
      canvas.getContext("webgl", {
        antialias: false,
        alpha: false,
        preserveDrawingBuffer: true,
      }) ||
      canvas.getContext("webgl2", {
        antialias: false,
        alpha: false,
        preserveDrawingBuffer: true,
      });

    if (!this.gl) {
      throw new Error("WebGL not supported");
    }

    // Set viewport
    this.gl.viewport(0, 0, canvas.width, canvas.height);
    this.shaderSource = this.#fragmtSrc;
  }

  get defaultSource() {
    return this.#fragmtSrc;
  }

  updateShader(source) {
    this.reset();
    this.shaderSource = source;
    this.setup();
    this.init();
  }

  updateScale(scale) {
    this.scale = scale;
    this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
  }

  compile(shader, source) {
    const gl = this.gl;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const error = gl.getShaderInfoLog(shader);
      console.error("❌ Shader compilation error:", error);
      throw new Error(`Shader compilation failed: ${error}`);
    }
  }

  test(source) {
    let result = null;
    const gl = this.gl;
    const shader = gl.createShader(gl.FRAGMENT_SHADER);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      result = gl.getShaderInfoLog(shader);
    }
    if (gl.getShaderParameter(shader, gl.DELETE_STATUS)) {
      gl.deleteShader(shader);
    }
    return result;
  }

  reset() {
    const { gl, program, vs, fs } = this;
    if (!gl) return;

    try {
      if (program && !gl.getProgramParameter(program, gl.DELETE_STATUS)) {
        if (vs && !gl.getShaderParameter(vs, gl.DELETE_STATUS)) {
          gl.detachShader(program, vs);
          gl.deleteShader(vs);
        }
        if (fs && !gl.getShaderParameter(fs, gl.DELETE_STATUS)) {
          gl.detachShader(program, fs);
          gl.deleteShader(fs);
        }
        gl.deleteProgram(program);
      }
    } catch (error) {
      console.warn("Error during WebGL cleanup:", error);
    }
  }

  setup() {
    const gl = this.gl;

    this.vs = gl.createShader(gl.VERTEX_SHADER);
    this.fs = gl.createShader(gl.FRAGMENT_SHADER);

    this.compile(this.vs, this.#vertexSrc);
    this.compile(this.fs, this.shaderSource);

    this.program = gl.createProgram();
    gl.attachShader(this.program, this.vs);
    gl.attachShader(this.program, this.fs);

    gl.linkProgram(this.program);

    if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
      const error = gl.getProgramInfoLog(this.program);
      console.error("❌ Program link error:", error);
      throw new Error(`Program linking failed: ${error}`);
    }
  }

  init() {
    const { gl, program } = this;
    this.buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(this.#vertices), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, "position");
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    program.resolution = gl.getUniformLocation(program, "resolution");
    program.time = gl.getUniformLocation(program, "time");
  }

  render(now = 0) {
    const { gl, program, buffer, canvas } = this;

    if (!program || !gl || gl.getProgramParameter(program, gl.DELETE_STATUS)) {
      return;
    }

    try {
      // Clear to black
      gl.clearColor(0.0, 0.0, 0.0, 1.0);
      gl.clear(gl.COLOR_BUFFER_BIT);

      gl.useProgram(program);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);

      // Set uniforms
      gl.uniform2f(program.resolution, canvas.width, canvas.height);
      gl.uniform1f(program.time, now * 1e-3);

      // Draw
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    } catch (error) {
      console.error("❌ WebGL render error:", error);
    }
  }
}

export default function Hyperspace({ isOpen, onClose, imageIndex = 0, spaceSoundIndex = 0 }) {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const currentAudioRef = useRef(null);
  const canvasRef = useRef(null);
  const rendererRef = useRef(null);

  // Array of original ship images
  const ships = [Ship, Ship1, Ship2];
  const currentShip = ships[imageIndex % ships.length];

  // Determine which effect to use based on imageIndex
  // Even numbers = hyperspace tunnel, Odd numbers = lightspeed effect
  const shouldUseLightspeed = imageIndex % 2 === 1;

  // Debug logging
  useEffect(() => {
    if (isOpen) {
      console.log(`🚀 Hyperspace opened - imageIndex: ${imageIndex}, shouldUseLightspeed: ${shouldUseLightspeed}`);
      console.log(`Ship image: ${currentShip}`);
    }
  }, [isOpen, imageIndex, shouldUseLightspeed, currentShip]);

  // Array of space sound files - using useMemo to prevent re-creation
  const spaceSounds = useMemo(
    () => [
      `${process.env.PUBLIC_URL}/audio/space-sound-1.mp3`,
      `${process.env.PUBLIC_URL}/audio/space-sound-2.mp3`,
      `${process.env.PUBLIC_URL}/audio/space-sound-3.mp3`,
    ],
    []
  );

  // Initialize lightspeed effect when needed
  useEffect(() => {
    if (isOpen && shouldUseLightspeed && canvasRef.current && !rendererRef.current) {
      const canvas = canvasRef.current;
      const dpr = Math.max(1, 0.5 * window.devicePixelRatio);
      const { innerWidth: width, innerHeight: height } = window;

      console.log("🔥 LIGHTSPEED MODE ACTIVATED!");
      console.log("Canvas element:", canvas);
      console.log("Canvas parent:", canvas.parentElement);
      console.log("Window size:", width, "x", height);

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = "100%";
      canvas.style.height = "100vh";

      try {
        console.log("✨ Initializing lightspeed tunnel effect");

        rendererRef.current = new Renderer(canvas, dpr);
        rendererRef.current.setup();
        rendererRef.current.init();

        // Start animation loop
        let animationRunning = true;
        const loop = (now) => {
          if (rendererRef.current && isOpen && shouldUseLightspeed && animationRunning) {
            rendererRef.current.render(now);
            requestAnimationFrame(loop);
          }
        };
        requestAnimationFrame(loop);

        console.log("🚀 Lightspeed tunnel animation started!");

        // Cleanup function
        return () => {
          animationRunning = false;
        };
      } catch (error) {
        console.error("❌ Lightspeed effect failed:", error);
        // Fallback - just show black screen
        rendererRef.current = null;
      }
    }

    // Cleanup lightspeed effect
    return () => {
      if (rendererRef.current && (!isOpen || !shouldUseLightspeed)) {
        try {
          rendererRef.current.reset();
        } catch (error) {
          console.warn("Error cleaning up renderer:", error);
        }
        rendererRef.current = null;
      }
    };
  }, [isOpen, shouldUseLightspeed]);

  // Handle window resize for lightspeed effect
  useEffect(() => {
    const handleResize = () => {
      if (rendererRef.current && canvasRef.current && shouldUseLightspeed) {
        const dpr = Math.max(1, 0.5 * devicePixelRatio);
        const canvas = canvasRef.current;
        const { innerWidth: width, innerHeight: height } = window;

        canvas.width = width * dpr;
        canvas.height = height * dpr;
        rendererRef.current.updateScale(dpr);
      }
    };

    if (isOpen && shouldUseLightspeed) {
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, [isOpen, shouldUseLightspeed]);

  // Handle smooth close
  const handleClose = useCallback(() => {
    // Stop current audio if it's playing
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }

    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200);
  }, [onClose]);

  // Auto-close timer effect (fallback in case audio fails)
  useEffect(() => {
    if (isOpen) {
      // Fallback timer in case audio doesn't load or fails to trigger 'ended' event
      const fallbackTimer = setTimeout(() => {
        handleClose();
      }, 15000); // 15 second fallback timeout

      return () => clearTimeout(fallbackTimer);
    }
  }, [isOpen, handleClose]);

  // Play space sound when hyperspace opens
  useEffect(() => {
    if (isOpen && spaceSounds.length > 0) {
      // Small delay to let the hyperspace button sound finish
      const timer = setTimeout(() => {
        const currentSpaceSound = spaceSounds[spaceSoundIndex % spaceSounds.length];
        const audio = new Audio(currentSpaceSound);
        audio.volume = 0.7;

        // Store the audio reference
        currentAudioRef.current = audio;

        audio.play().catch((error) => {
          console.log("Space sound play failed:", error);
        });

        // Close hyperspace when audio ends naturally (but only if user hasn't manually closed)
        audio.addEventListener("ended", () => {
          if (currentAudioRef.current === audio) {
            currentAudioRef.current = null;
            handleClose();
          }
        });
      }, 300); // 300ms delay after hyperspace opens

      return () => clearTimeout(timer);
    }
  }, [isOpen, spaceSoundIndex, spaceSounds, handleClose]);

  // Handle keyboard events (Escape key)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, handleClose]);

  // Handle scroll locking when hyperspace is open
  useEffect(() => {
    if (isOpen) {
      // Store current scroll position
      const currentScroll = window.scrollY;
      setScrollPosition(currentScroll);

      // Lock the body
      document.body.style.position = "fixed";
      document.body.style.top = `-${currentScroll}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";

      // Dispatch custom event for StarField to react to
      window.dispatchEvent(new CustomEvent("hyperspace-opened"));
    } else {
      // Stop audio when hyperspace closes
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
        currentAudioRef.current = null;
      }

      // Delay the body style restoration to prevent white flash
      setTimeout(() => {
        // Restore scrolling and position
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.style.overflow = "";

        // Restore scroll position
        if (scrollPosition) {
          window.scrollTo(0, scrollPosition);
        }

        // Dispatch custom event after body styles are restored
        window.dispatchEvent(new CustomEvent("hyperspace-closed"));
      }, 250); // Slight delay after fade-out completes
    }

    // Cleanup on unmount
    return () => {
      // Stop audio on cleanup
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
        currentAudioRef.current = null;
      }

      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
    };
  }, [isOpen, scrollPosition]);

  if (!isOpen) return null;

  return (
    <div
      className={`hyperspace-overlay ${isClosing ? "fade-out" : ""}`}
      onClick={handleClose}
      onKeyDown={(e) => e.key === "Enter" && handleClose()}
      role="presentation"
      tabIndex={-1}
      aria-label="Close hyperspace effect"
    >
      {shouldUseLightspeed ? (
        // Lightspeed tunnel effect WITH ship
        <>
          <canvas
            ref={canvasRef}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100vh",
              display: "block",
              background: "#000",
              userSelect: "none",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
          {/* Ship in lightspeed mode too */}
          <div className="hyperspace-ship">
            <img
              src={currentShip}
              alt="Ship"
              className="ship-image"
              style={{
                width: "200px",
                height: "auto",
                display: "block",
                filter: "brightness(1.3) contrast(1.2) saturate(1.5) drop-shadow(0 0 20px rgba(255,255,255,0.8))",
              }}
            />
          </div>
        </>
      ) : (
        // Original hyperspace tunnel with ships
        <>
          <div className="scene">
            <div className="wrap">
              <div className="wall wall-right"></div>
              <div className="wall wall-left"></div>
              <div className="wall wall-top"></div>
              <div className="wall wall-bottom"></div>
              <div className="wall wall-back"></div>
            </div>
            <div className="wrap">
              <div className="wall wall-right"></div>
              <div className="wall wall-left"></div>
              <div className="wall wall-top"></div>
              <div className="wall wall-bottom"></div>
              <div className="wall wall-back"></div>
            </div>
          </div>

          {/* Original ship in the center */}
          <div className="hyperspace-ship">
            <img
              src={currentShip}
              alt="Ship"
              className="ship-image"
              style={{
                width: "200px",
                height: "auto",
                display: "block",
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}

// PropTypes validation
Hyperspace.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  imageIndex: PropTypes.number,
  spaceSoundIndex: PropTypes.number,
};
